import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { Mail, MessageCircleQuestion, Receipt, Send } from "lucide-react";
import { apiGet, queryClient } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { QueryError } from "@/components/shared/query-error";

type MessageType = "reminder" | "ask_client" | "ask_receipt" | "invite";

interface AccountantMessage {
  id: string;
  type: MessageType;
  subject: string;
  body_html: string;
  created_at: string;
  linked_receipt_id: string | null;
  linked_receipt_vendor: string;
  linked_receipt_total: string | null;
  sent_by_email: string;
}

interface MessagesResponse {
  results: AccountantMessage[];
  total_count: number;
  has_more: boolean;
}

const TYPE_LABEL: Record<MessageType, string> = {
  reminder: "Reminder",
  ask_client: "Question",
  ask_receipt: "Receipt question",
  invite: "Invite",
};

function TypeIcon({ type }: { type: MessageType }) {
  const Icon =
    type === "reminder"
      ? Mail
      : type === "ask_receipt"
        ? Receipt
        : type === "invite"
          ? Send
          : MessageCircleQuestion;
  return (
    <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
      <Icon className="size-4" aria-hidden />
    </div>
  );
}

export function MessagesTab({ userId }: { userId: string }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["accountant-messages", userId],
    queryFn: () =>
      apiGet<MessagesResponse>(
        `/api/accountant/users/${userId}/messages?limit=50&offset=0`,
      ),
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="flex items-start gap-3 rounded-lg border p-4"
          >
            <Skeleton className="size-8 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/3" />
              <Skeleton className="h-12 w-full" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <QueryError
        error={error}
        message="Could not load message history"
        onRetry={() =>
          queryClient.invalidateQueries({
            queryKey: ["accountant-messages", userId],
          })
        }
      />
    );
  }

  const messages = data?.results ?? [];

  if (messages.length === 0) {
    return (
      <div className="rounded-lg border py-12 text-center text-sm text-muted-foreground">
        No messages sent yet. Send a reminder or ask a question about a
        receipt and it will show up here.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {messages.map((m) => (
        <MessageItem key={m.id} message={m} />
      ))}
    </div>
  );
}

function MessageItem({ message }: { message: AccountantMessage }) {
  const sentAgo = (() => {
    try {
      return formatDistanceToNow(new Date(message.created_at), {
        addSuffix: true,
      });
    } catch {
      return "";
    }
  })();

  return (
    <details className="group rounded-lg border">
      <summary className="flex cursor-pointer list-none items-start gap-3 p-4 hover:bg-muted/30">
        <TypeIcon type={message.type} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="text-[10px] capitalize">
              {TYPE_LABEL[message.type]}
            </Badge>
            <p className="font-medium text-sm truncate">{message.subject}</p>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Sent {sentAgo}
            {message.sent_by_email ? ` · ${message.sent_by_email}` : ""}
            {message.linked_receipt_vendor
              ? ` · Re: ${message.linked_receipt_vendor}`
              : ""}
          </p>
        </div>
        <span className="text-xs text-muted-foreground group-open:hidden">
          View
        </span>
        <span className="hidden text-xs text-muted-foreground group-open:inline">
          Hide
        </span>
      </summary>
      <div className="border-t bg-muted/20 p-4">
        {message.body_html ? (
          // The body_html is server-rendered from our own templates — safe
          // to inject. Sanitise here too if we ever accept user-authored HTML.
          <div
            className="prose prose-sm max-w-none text-sm"
            dangerouslySetInnerHTML={{ __html: message.body_html }}
          />
        ) : (
          <p className="text-xs text-muted-foreground">
            No body captured for this message.
          </p>
        )}
      </div>
    </details>
  );
}
